import { useEffect, useRef, useState } from "react";
import * as Cesium from "cesium";
import { CircleDot, Layers3, MapPin, Maximize2, ZoomIn, ZoomOut } from "lucide-react";
import scenarioData from "../data/scenario.json";
import "cesium/Build/Cesium/Widgets/widgets.css";

const CESIUM_ACCESS_TOKEN = import.meta.env.CESIUM_ACCESS_TOKEN;
if (CESIUM_ACCESS_TOKEN) Cesium.Ion.defaultAccessToken = CESIUM_ACCESS_TOKEN;

const MAP_BOUNDS = {
  west: 93.35,
  east: 94.55,
  south: 28.1,
  north: 29.15,
};

function normalizedToCartesian([x, y], altitude = 30) {
  const longitude = MAP_BOUNDS.west + (x / 100) * (MAP_BOUNDS.east - MAP_BOUNDS.west);
  const latitude = MAP_BOUNDS.north - (y / 100) * (MAP_BOUNDS.north - MAP_BOUNDS.south);
  return Cesium.Cartesian3.fromDegrees(longitude, latitude, altitude);
}

function entityColor(entity) {
  if (entity.type === "UNKNOWN") return Cesium.Color.fromCssColorString("#be9aff");
  if (entity.type === "UAV" || entity.type === "AIRCRAFT") return Cesium.Color.fromCssColorString("#51d7cf");
  return Cesium.Color.fromCssColorString("#f1b466");
}

function layerColor(entity) {
  return entityColor(entity).withAlpha(0.88);
}

export default function CesiumMap({ entities, selectedId, onSelect, layers, paused, onToast, onToggleLayer }) {
  const containerRef = useRef(null);
  const viewerRef = useRef(null);
  const dataSourcesRef = useRef(null);
  const onSelectRef = useRef(onSelect);
  const onToastRef = useRef(onToast);
  const [imageryMode, setImageryMode] = useState("OPENSTREETMAP IMAGERY");
  const [terrainMode, setTerrainMode] = useState(CESIUM_ACCESS_TOKEN ? "LOADING WORLD TERRAIN" : "ELLIPSOID TERRAIN");

  useEffect(() => {
    onSelectRef.current = onSelect;
  }, [onSelect]);

  useEffect(() => {
    onToastRef.current = onToast;
  }, [onToast]);

  useEffect(() => {
    if (!containerRef.current) return undefined;

    const viewer = new Cesium.Viewer(containerRef.current, {
      animation: false,
      baseLayerPicker: false,
      fullscreenButton: false,
      geocoder: false,
      homeButton: false,
      infoBox: false,
      sceneModePicker: false,
      selectionIndicator: false,
      timeline: false,
      navigationHelpButton: false,
      terrainProvider: new Cesium.EllipsoidTerrainProvider(),
      baseLayer: false,
    });

    let imageryCancelled = false;
    Cesium.TileMapServiceImageryProvider.fromUrl(Cesium.buildModuleUrl("Assets/Textures/NaturalEarthII/"))
      .then((localProvider) => {
        if (imageryCancelled || viewer.isDestroyed()) return;
        viewer.imageryLayers.addImageryProvider(localProvider, 0);
        viewer.scene.requestRender();
      })
      .catch(() => {
        setImageryMode("NO LOCAL IMAGERY AVAILABLE");
        onToastRef.current("Bundled fallback imagery could not be loaded");
      });

    let terrainCancelled = false;
    if (CESIUM_ACCESS_TOKEN) {
      Cesium.createWorldTerrainAsync({ requestVertexNormals: true, requestWaterMask: true })
        .then((terrainProvider) => {
          if (terrainCancelled || viewer.isDestroyed()) return;
          viewer.terrainProvider = terrainProvider;
          viewer.scene.verticalExaggeration = 1.35;
          setTerrainMode("WORLD TERRAIN / 1.35X RELIEF");
          onToastRef.current("Cesium World Terrain loaded");
          viewer.scene.requestRender();
        })
        .catch(() => {
          setTerrainMode("ELLIPSOID TERRAIN");
          onToastRef.current("World Terrain unavailable — using ellipsoid fallback");
        });
    }

    const osmProvider = new Cesium.OpenStreetMapImageryProvider({ url: "https://tile.openstreetmap.org/" });
    const osmLayer = viewer.imageryLayers.addImageryProvider(osmProvider, 0);
    osmProvider.errorEvent.addEventListener(() => {
      if (!osmLayer.show) return;
      osmLayer.show = false;
      setImageryMode("BUNDLED NATURAL EARTH FALLBACK");
      onToastRef.current("External map tiles unavailable — using bundled fallback imagery");
    });

    viewer.scene.backgroundColor = Cesium.Color.fromCssColorString("#09100f");
    viewer.scene.globe.baseColor = Cesium.Color.fromCssColorString("#0c1515");
    viewer.scene.globe.enableLighting = true;
    viewer.scene.globe.depthTestAgainstTerrain = false;
    viewer.scene.fog.enabled = true;
    viewer.scene.fog.density = 0.00004;
    viewer.scene.skyAtmosphere.show = true;
    viewer.scene.globe.showGroundAtmosphere = true;
    viewer.scene.requestRenderMode = false;

    const focus = Cesium.Cartesian3.fromDegrees(93.951918, 28.635522, 0);
    viewer.camera.lookAt(focus, new Cesium.HeadingPitchRange(Cesium.Math.toRadians(4), Cesium.Math.toRadians(-53), 106000));

    const dataSources = {
      entities: new Cesium.CustomDataSource("northframe-entities"),
      tracks: new Cesium.CustomDataSource("northframe-tracks"),
      zone: new Cesium.CustomDataSource("northframe-zones"),
      observations: new Cesium.CustomDataSource("northframe-observations"),
    };

    Object.values(dataSources).forEach((dataSource) => viewer.dataSources.add(dataSource));

    const handler = new Cesium.ScreenSpaceEventHandler(viewer.scene.canvas);
    handler.setInputAction((movement) => {
      const picked = viewer.scene.pick(movement.position);
      const pickedId = picked?.id?.northframeId;
      if (pickedId) onSelectRef.current(pickedId);
    }, Cesium.ScreenSpaceEventType.LEFT_CLICK);

    viewerRef.current = viewer;
    dataSourcesRef.current = dataSources;

    return () => {
      imageryCancelled = true;
      terrainCancelled = true;
      handler.destroy();
      if (!viewer.isDestroyed()) viewer.destroy();
      viewerRef.current = null;
      dataSourcesRef.current = null;
    };
  }, []);

  useEffect(() => {
    const viewer = viewerRef.current;
    const dataSources = dataSourcesRef.current;
    if (!viewer || !dataSources) return;

    Object.values(dataSources).forEach((dataSource) => dataSource.entities.removeAll());
    dataSources.entities.show = layers.entities;
    dataSources.tracks.show = layers.tracks;
    dataSources.zone.show = layers.zone;
    dataSources.observations.show = layers.observations;

    if (layers.zone) {
      const zone = scenarioData.zones[0];
      const zonePositions = zone.points.map((point) => normalizedToCartesian(point, 30));
      dataSources.zone.entities.add({
        id: zone.id,
        polygon: {
          hierarchy: zonePositions,
          material: Cesium.Color.fromCssColorString("#f4b45f").withAlpha(0.15),
          outline: true,
          outlineColor: Cesium.Color.fromCssColorString("#f4b45f").withAlpha(0.86),
          outlineWidth: 2,
          height: 0,
          heightReference: Cesium.HeightReference.CLAMP_TO_GROUND,
          extrudedHeight: 75,
          extrudedHeightReference: Cesium.HeightReference.RELATIVE_TO_GROUND,
        },
        label: {
          text: "BRAVO MONITORING ZONE",
          position: normalizedToCartesian([48, 67], 90),
          fillColor: Cesium.Color.fromCssColorString("#f4b45f"),
          font: "10px 'Cascadia Code', monospace",
          style: Cesium.LabelStyle.FILL,
          outlineWidth: 3,
          outlineColor: Cesium.Color.fromCssColorString("#0b1110"),
          pixelOffset: new Cesium.Cartesian2(0, -2),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
    }

    entities.forEach((entity) => {
      const color = layerColor(entity);
      const effectiveFreshness = paused && entity.source_id === "SYNTH-DEMO-ALPHA" ? "STALE" : entity.freshness;
      const position = Cesium.Cartesian3.fromDegrees(entity.position.lon, entity.position.lat, Math.max(entity.position.altitude_m || 0, 20));
      const cesiumEntity = dataSources.entities.entities.add({
        id: `entity-${entity.id}`,
        position,
        point: {
          pixelSize: entity.id === selectedId ? 15 : 10,
          color,
          outlineColor: color.withAlpha(0.28),
          outlineWidth: entity.id === selectedId ? 6 : 3,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
        label: {
          text: entity.id,
          font: entity.id === selectedId ? "bold 11px 'Cascadia Code', monospace" : "10px 'Cascadia Code', monospace",
          fillColor: entity.id === selectedId ? Cesium.Color.WHITE : color,
          style: Cesium.LabelStyle.FILL,
          outlineWidth: 4,
          outlineColor: Cesium.Color.fromCssColorString("#09100f").withAlpha(0.9),
          pixelOffset: new Cesium.Cartesian2(16, -12),
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
      cesiumEntity.northframeId = entity.id;

      if (entity.id === selectedId) {
        dataSources.entities.entities.add({
          id: `selection-${entity.id}`,
          position,
          ellipse: {
            semiMajorAxis: 5400,
            semiMinorAxis: 5400,
            height: Math.max(entity.position.altitude_m || 0, 20) + 80,
            material: color.withAlpha(0.1),
            outline: true,
            outlineColor: color,
            outlineWidth: 2,
          },
        });
      }

      if (layers.tracks && entity.trail?.length > 1 && entity.type !== "UNKNOWN") {
        const trailPositions = entity.trail.map((point) => normalizedToCartesian(point, Math.max(entity.position.altitude_m || 0, 20) + 60));
        dataSources.tracks.entities.add({
          id: `track-${entity.id}`,
          polyline: {
            positions: trailPositions,
            width: entity.id === selectedId ? 3.5 : 2,
            material: new Cesium.PolylineDashMaterialProperty({
              color: entity.id === selectedId ? Cesium.Color.fromCssColorString("#2ee4c0") : color.withAlpha(0.55),
              dashLength: 12,
            }),
            clampToGround: false,
          },
        });
      }

      if (layers.observations && entity.type === "UNKNOWN") {
        dataSources.observations.entities.add({
          id: `observation-${entity.id}`,
          position,
          ellipse: {
            semiMajorAxis: 7600,
            semiMinorAxis: 7600,
            height: 45,
            material: Cesium.Color.fromCssColorString("#be9aff").withAlpha(0.05),
            outline: true,
            outlineColor: Cesium.Color.fromCssColorString("#be9aff").withAlpha(0.8),
            outlineWidth: 2,
          },
        });
      }

      if (effectiveFreshness === "STALE") {
        dataSources.entities.entities.add({
          id: `stale-${entity.id}`,
          position: Cesium.Cartesian3.fromDegrees(entity.position.lon, entity.position.lat, Math.max(entity.position.altitude_m || 0, 20) + 170),
          label: {
            text: "SOURCE PAUSED",
            font: "8px 'Cascadia Code', monospace",
            fillColor: Cesium.Color.fromCssColorString("#f4b45f"),
            style: Cesium.LabelStyle.FILL,
            outlineWidth: 3,
            outlineColor: Cesium.Color.fromCssColorString("#09100f"),
            pixelOffset: new Cesium.Cartesian2(17, 4),
            disableDepthTestDistance: Number.POSITIVE_INFINITY,
          },
        });
      }
    });

    viewer.scene.requestRender();
  }, [entities, selectedId, layers, paused]);

  const zoom = (amount) => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    if (amount > 0) viewer.camera.zoomIn(15000);
    else viewer.camera.zoomOut(15000);
    onToast(amount > 0 ? "Zoomed in" : "Zoomed out");
  };

  const center = () => {
    const viewer = viewerRef.current;
    if (!viewer) return;
    viewer.camera.flyTo({
      destination: Cesium.Cartesian3.fromDegrees(93.951918, 28.635522, 106000),
      orientation: { heading: Cesium.Math.toRadians(4), pitch: Cesium.Math.toRadians(-53), roll: 0 },
      duration: 0.8,
    });
    onToast("Map centered on operating area");
  };

  return (
    <div className="map-surface cesium-map-surface">
      <div ref={containerRef} className="cesium-container" aria-label="3D Cesium map of the Demo Alpha operating area" />
      <div className="map-legend">
        <span className="legend-label">3D LAYER STACK</span>
        {[
          { id: "entities", label: "Entities", className: "entity-swatch", icon: MapPin },
          { id: "tracks", label: "Tracks", className: "track-swatch", icon: Layers3 },
          { id: "zone", label: "Bravo zone", className: "zone-swatch", icon: Layers3 },
          { id: "observations", label: "Observations", className: "obs-swatch", icon: CircleDot },
        ].map(({ id, label, className, icon: Icon }) => (
          <button key={id} className={`layer-toggle ${layers[id] ? "is-on" : ""}`} type="button" onClick={() => { onToggleLayer(id); onToast(`${label} layer ${layers[id] ? "hidden" : "visible"}`); }}>
            <span className={`layer-swatch ${className}`}><Icon size={10} strokeWidth={2.5} /></span>{label}
          </button>
        ))}
      </div>
      <div className="map-readout"><span>28° 38′ N</span><span>93° 57′ E</span></div>
      <div className="map-scale"><span></span><b>20 km</b></div>
      <div className="map-controls"><button type="button" aria-label="Zoom in" onClick={() => zoom(1)}><ZoomIn size={16} /></button><button type="button" aria-label="Zoom out" onClick={() => zoom(-1)}><ZoomOut size={16} /></button><button type="button" aria-label="Center map" onClick={center}><Maximize2 size={15} /></button></div>
      <div className="map-corner map-corner-top-left"><span className="corner-marker"></span> 3D VIEW / {terrainMode}</div>
      <div className="map-corner map-corner-bottom-left">CESIUM GLOBE / {imageryMode}</div>
    </div>
  );
}
